import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Activity, Loader2, RefreshCw, AlertTriangle, BookOpen, Layers, Target } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WAVES = [
  { n: "1", char_en: "Weak initial move, hard to spot", char_ar: "حركة بدئية ضعيفة، صعبة الرصد", action_en: "Watch for setup", action_ar: "راقب التشكل", color: "bg-blue-100 text-blue-700" },
  { n: "2", char_en: "Sharp pullback 50–61.8%", char_ar: "ارتداد حاد 50-61.8%", action_en: "BUY — best R:R", action_ar: "شراء — أفضل نسبة مخاطرة", color: "bg-emerald-100 text-emerald-700" },
  { n: "3", char_en: "Strongest move, money wave", char_ar: "أقوى موجة — موجة الربح", action_en: "HOLD longs", action_ar: "احتفظ بالشراء", color: "bg-green-100 text-green-700" },
  { n: "4", char_en: "Mild sideways correction", char_ar: "تصحيح جانبي خفيف", action_en: "BUY dips", action_ar: "اشتر الانخفاضات", color: "bg-teal-100 text-teal-700" },
  { n: "5", char_en: "Weak rally + RSI divergence", char_ar: "ارتفاع ضعيف + دايفرجنس", action_en: "EXIT longs", action_ar: "اخرج من الشراء", color: "bg-orange-100 text-orange-700" },
  { n: "A", char_en: "First corrective leg", char_ar: "أول موجة تصحيحية", action_en: "Exit remaining", action_ar: "اخرج تماماً", color: "bg-red-100 text-red-700" },
  { n: "B", char_en: "False recovery rally", char_ar: "ارتداد كاذب", action_en: "DO NOT BUY — trap", action_ar: "لا تشتري — فخ", color: "bg-rose-100 text-rose-700" },
  { n: "C", char_en: "Powerful final sell-off", char_ar: "بيع نهائي قوي", action_en: "BUY at bottom", action_ar: "اشتر عند القاع", color: "bg-pink-100 text-pink-700" },
];

const RULES = {
  en: [
    "Wave 2 NEVER retraces below the start of Wave 1",
    "Wave 3 is NEVER the shortest impulse wave",
    "Wave 4 NEVER overlaps Wave 1's territory",
  ],
  ar: [
    "الموجة 2 لا تتجاوز أبداً بداية الموجة 1",
    "الموجة 3 ليست أبداً أقصر موجة دافعة",
    "الموجة 4 لا تدخل أبداً منطقة الموجة 1",
  ],
};

export function ElliottWavePage({ lang, symbol = "XAU/USD", timeframe = "4h" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isAr = lang === "ar";

  const fetchSignal = async () => {
    setLoading(true);
    setError("");
    try {
      const urlSymbol = symbol.replace("/", "-");
      const res = await axios.get(`${API}/elliott-wave/${urlSymbol}`, { params: { timeframe } });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || (isAr ? "تعذّر جلب التحليل" : "Failed to fetch analysis"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSignal(); /* eslint-disable-next-line */ }, [symbol, timeframe]);

  const sigColor = data?.signal === "BUY" ? "bg-emerald-500" : data?.signal === "SELL" ? "bg-red-500" : "bg-zinc-400";

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="space-y-4">
      <div className="flex items-center gap-2">
        <Activity className="w-6 h-6 text-purple-500" />
        <h1 className="text-xl sm:text-2xl font-extrabold text-pink-600">
          {isAr ? "التحليل الموجي — Elliott Wave" : "Elliott Wave Analysis"}
        </h1>
      </div>

      {/* Live wave detection */}
      <Card className="bg-white border-4 border-purple-300 rounded-3xl overflow-hidden shadow-lg shadow-purple-200/40">
        <div className="h-2 bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400" />
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-500" />
              <h3 className="font-bold text-sm sm:text-base text-zinc-800">
                {isAr ? "الموجة الحالية" : "Current Wave"}
              </h3>
              <Badge variant="outline" className="text-[10px] border-purple-300 text-purple-700">
                {symbol} · {timeframe}
              </Badge>
            </div>
            <Button variant="ghost" size="icon" onClick={fetchSignal} disabled={loading} className="text-purple-500 h-8 w-8">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>

          {loading && !data && (
            <div className="flex items-center justify-center py-8 text-purple-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span className="text-sm">{isAr ? "جاري تحليل الموجات..." : "Analyzing waves..."}</span>
            </div>
          )}

          {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-xs">{error}</div>}

          {data && (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 tracking-wide">{isAr ? "الموجة" : "Wave"}</p>
                  <p className="text-lg sm:text-xl font-extrabold text-purple-700">{data.wave_label}</p>
                  <p className="text-xs text-zinc-600 mt-1">{isAr ? "التوصية:" : "Recommendation:"} <strong>{data.recommendation}</strong></p>
                </div>
                <div className="text-right">
                  <Badge className={`${sigColor} text-white text-base px-4 py-2 rounded-full font-bold`}>{data.signal}</Badge>
                  <p className="text-xs text-zinc-500 mt-1">{isAr ? "الثقة" : "Confidence"} <strong className="text-pink-600">{data.confidence}%</strong></p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-3">
                <Box label={isAr ? "السعر" : "Price"} value={data.current_price} color="zinc" />
                <Box label={isAr ? "الدخول" : "Entry"} value={data.entry} color="blue" />
                <Box label="SL" value={data.stop_loss} color="red" />
                <Box label="TP" value={data.take_profit_1} color="emerald" />
              </div>

              {data.rsi !== undefined && (
                <p className="text-xs text-zinc-600 mb-2">RSI: <strong className="text-purple-700">{data.rsi}</strong></p>
              )}

              {data.notes?.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-2.5 text-xs text-purple-800 space-y-1">
                  {data.notes.map((n, i) => <p key={i}>• {n}</p>)}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* 8-Wave Reference Table */}
      <Card className="bg-white border-2 border-pink-200 rounded-2xl overflow-hidden">
        <div className="h-1 bg-pink-400" />
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-5 h-5 text-pink-500" />
            <h3 className="font-bold text-sm sm:text-base text-zinc-800">{isAr ? "دورة الموجات الثمانية" : "The 8-Wave Cycle"}</h3>
          </div>
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-pink-100 text-zinc-500">
                  <th className={`py-2 px-2 ${isAr ? "text-right" : "text-left"} font-semibold`}>{isAr ? "الموجة" : "Wave"}</th>
                  <th className={`py-2 px-2 ${isAr ? "text-right" : "text-left"} font-semibold`}>{isAr ? "الخاصية" : "Character"}</th>
                  <th className={`py-2 px-2 ${isAr ? "text-right" : "text-left"} font-semibold`}>{isAr ? "الإجراء" : "Action"}</th>
                </tr>
              </thead>
              <tbody>
                {WAVES.map((w) => (
                  <tr key={w.n} className="border-b border-pink-50 last:border-0">
                    <td className="py-2 px-2"><Badge className={`${w.color} border-0`}>{w.n}</Badge></td>
                    <td className="py-2 px-2 text-zinc-700">{isAr ? w.char_ar : w.char_en}</td>
                    <td className="py-2 px-2 font-semibold text-zinc-800">{isAr ? w.action_ar : w.action_en}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Rules */}
      <Card className="bg-red-50 border-2 border-red-200 rounded-2xl">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h3 className="font-bold text-sm sm:text-base text-red-700">{isAr ? "القواعد الثلاث التي لا تُكسر" : "Three Unbreakable Rules"}</h3>
          </div>
          <ul className="space-y-1.5 text-xs sm:text-sm text-red-800 list-decimal list-inside">
            {RULES[lang].map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </CardContent>
      </Card>

      {/* Methodology */}
      <Card className="bg-white border-2 border-cyan-200 rounded-2xl">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-5 h-5 text-cyan-500" />
            <h3 className="font-bold text-sm sm:text-base text-zinc-800">{isAr ? "منهجية التحليل من 6 خطوات" : "6-Step Analysis Methodology"}</h3>
          </div>
          <ol className="space-y-1.5 text-xs sm:text-sm text-zinc-700 list-decimal list-inside">
            <li>{isAr ? "حدد الاتجاه: قمم وقيعان أعلى = صاعد" : "Identify trend: higher highs + lows = uptrend"}</li>
            <li>{isAr ? "ابحث عن نقطة البداية (آخر قاع/قمة مهمة)" : "Find starting point (most recent significant swing)"}</li>
            <li>{isAr ? "اعد الموجات 1 إلى 5 وتحقق من القواعد" : "Count waves 1–5, verify rules"}</li>
            <li>{isAr ? "طبق Fibonacci على كل موجة" : "Apply Fibonacci to each wave"}</li>
            <li>{isAr ? "ابحث عن مناطق التوافق (Confluence)" : "Find confluence zones"}</li>
            <li>{isAr ? "انتظر التأكيد قبل الدخول" : "Wait for confirmation before entering"}</li>
          </ol>
        </CardContent>
      </Card>

      <div className="text-center text-xs text-zinc-500 py-3">
        <Activity className="w-4 h-4 inline mr-1 text-purple-400" />
        {isAr ? "التحليل الموجي يحتاج تأكيدات إضافية — استخدم RSI + شموع + Fibonacci" : "Elliott Wave requires confirmations — use RSI + candles + Fibonacci"}
      </div>
    </div>
  );
}

function Box({ label, value, color }) {
  const bg = { zinc: "bg-zinc-50 text-zinc-800", blue: "bg-blue-50 text-blue-700", red: "bg-red-50 text-red-600", emerald: "bg-emerald-50 text-emerald-700" }[color];
  return (
    <div className={`${bg} rounded-xl p-2 text-center`}>
      <p className="text-[10px] text-zinc-500">{label}</p>
      <p className="text-xs sm:text-sm font-mono font-bold">{value}</p>
    </div>
  );
}
